/* eslint-disable camelcase */
import { FC, useEffect, useState } from 'react';

import {
  Input,
  FormField,
  Button,
  Heading,
  Text,
  Form,
} from '@livechat/design-system-react-components';

import Config from 'Config';
import { Layout } from 'components/layout/Layout';
import { FormStates } from 'types/form';
import Location from 'services/Location';
import { isEmailValid, validateEmail } from 'helpers/validators/email';
import { detectProduct, getAppLinkByName, getProductName } from 'helpers/product';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';

import * as styles from './styles';

export const CustomSSO: FC = () => {
  const [email, setEmail] = useState<string>('');
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [formState, setFormState] = useState<string>(FormStates.Default);

  const clientId = Location.getUrlParam('client_id') || '';
  const redirectUri = Location.getUrlParam('redirect_uri') || '';
  const responseType = Location.getUrlParam('response_type') || '';
  const state = Location.getUrlParam('state') || '';
  const codeChallenge = Location.getUrlParam('code_challenge') || '';
  const codeVerifier = Location.getUrlParam('code_verifier') || '';
  const prompt = Location.getUrlParam('prompt') || '';

  useEffect(() => {
    const emailParam = Location.getNthUrlParam(1) || Location.getUrlParam('entity_id');

    if (isEmailValid(emailParam)) {
      setEmail(emailParam);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailValidation = validateEmail(email);

    if (emailValidation) {
      setEmailValidationError(emailValidation);
      return;
    }

    setFormState(FormStates.Requesting);

    const product = detectProduct();
    if (product) {
      pushExternalRedirectToDataLayer(getAppLinkByName(getProductName(product)));
    }

    let loginUrl: string = `${Config.accounts_api_url}/v2/saml/login?email=${encodeURIComponent(email)}`;
    loginUrl += `&client_id=${clientId}`;
    loginUrl += `&redirect_uri=${redirectUri}`;
    loginUrl += `&response_type=${responseType}`;
    loginUrl += `${state ? `&state=${state}` : ''}`;
    loginUrl += `${codeChallenge ? `&code_challenge=${codeChallenge}` : ''}`;
    loginUrl += `${codeVerifier ? `&code_verifier=${codeVerifier}` : ''}`;
    loginUrl += `${prompt ? `&prompt=${prompt}` : ''}`;

    window.location.href = loginUrl;
  };

  const handleEmailInputChange = (e) => {
    if (!validateEmail(e.target.value)) {
      setEmailValidationError(null);
      setFormState(FormStates.Default);
    }

    setEmail(e.target.value);
  };

  return (
    <Layout showHeaderLogo showGoToSignIn>
      <div css={styles.container} data-testid="custom-sso-container">
        <Heading size="lg">Log in using Single Sign-On (SSO)</Heading>
        <Text>
          Enter the email associated with your SSO account, <br />
          such as Okta or OneLogin.
        </Text>
        <Form onSubmit={handleSubmit}>
          <FormField
            labelText="Email"
            labelFor="email"
            css={styles.formField}
            error={emailValidationError}
          >
            <Input
              type="email"
              name="email"
              placeholder="name@work-email.com"
              value={email}
              onChange={handleEmailInputChange}
              inputSize="large"
              error={!!emailValidationError}
            />
          </FormField>
          <Button
            size="large"
            kind="primary"
            css={styles.button}
            onClick={handleSubmit}
            loading={formState === FormStates.Requesting}
          >
            Log in
          </Button>
        </Form>
      </div>
    </Layout>
  );
};
